import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import CaptchaChallenge from '../components/CaptchaChallenge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const handleCaptchaValidate = (isValid, token) => {
    setCaptchaValid(isValid);
    setCaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!captchaValid) {
      toast.error('Veuillez compléter la vérification anti-spam');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        timestamp: Math.floor(Date.now() / 1000),
        captcha_token: captchaToken
      });
      toast.success('Message envoyé avec succès !');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setCaptchaValid(false);
      setCaptchaToken(null);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSubmitting(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact_address'),
      content: 'Yaoundé, Quartier Omnisport\nCameroun'
    },
    {
      icon: Phone,
      title: t('contact_phone'),
      content: '+237 6 86 89 58 17',
      href: 'tel:+237686895817'
    },
    {
      icon: Mail,
      title: t('contact_email'),
      content: 'contact@gticsarl.com',
      href: 'mailto:contact@gticsarl.com'
    },
    {
      icon: Clock,
      title: 'Horaires',
      content: 'Lun - Ven: 8h00 - 17h00\nSam: 9h00 - 13h00'
    }
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <span className="inline-block px-4 py-1 bg-[#0B6AB3]/20 text-[#0B6AB3] text-sm font-medium rounded-full mb-4">
              Parlons de votre projet
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white font-['Montserrat']">
              {t('contact_title')}
            </h1>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto text-lg">
              {t('contact_subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              {contactInfo.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInUp}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="border-0 shadow-sm" data-testid={`contact-info-${index}`}>
                      <CardContent className="p-5 flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#0B6AB3]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent className="h-6 w-6 text-[#0B6AB3]" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#0F172A]">{item.title}</h4>
                          {item.href ? (
                            <a href={item.href} className="text-gray-600 hover:text-[#0B6AB3] whitespace-pre-line">
                              {item.content}
                            </a>
                          ) : (
                            <p className="text-gray-600 whitespace-pre-line">{item.content}</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}

              {/* WhatsApp Button */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: 0.4 }}
              >
                <a
                  href="https://wa.me/237686895817"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 w-full py-4 bg-[#25D366] text-white rounded-lg font-medium hover:bg-[#20bd5a] transition-colors"
                  data-testid="whatsapp-contact"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Discuter sur WhatsApp
                </a>
              </motion.div>
            </div>

            {/* Contact Form */}
            <motion.div
              className="lg:col-span-2"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <Card className="border-0 shadow-lg" data-testid="contact-form">
                <CardContent className="p-6 md:p-8">
                  <h3 className="text-2xl font-bold text-[#0F172A] mb-6 font-['Montserrat']">
                    Envoyez-nous un message
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name">{t('contact_name')} *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="mt-1"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">{t('contact_email')} *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="mt-1"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="phone">{t('contact_phone')}</Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="mt-1"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject">{t('contact_subject')} *</Label>
                        <Input
                          id="subject"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          required
                          className="mt-1"
                          data-testid="contact-subject"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="message">{t('contact_message')} *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        rows={5}
                        className="mt-1"
                        placeholder="Décrivez votre projet ou votre demande..."
                        data-testid="contact-message"
                      />
                    </div>
                    {/* Captcha Challenge */}
                    <CaptchaChallenge onValidate={handleCaptchaValidate} />
                    {/* Honeypot */}
                    <input type="text" name="website" style={{ display: 'none' }} tabIndex={-1} />
                    <Button
                      type="submit"
                      className="w-full bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
                      disabled={submitting || !captchaValid}
                      data-testid="contact-submit"
                    >
                      {submitting ? 'Envoi en cours...' : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          {t('contact_send')}
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-96 bg-gray-200" data-testid="map-section">
        <iframe
          title="GTIC SARL Location"
          src="https://www.openstreetmap.org/export/embed.html?bbox=11.5000%2C3.8500%2C11.5500%2C3.9000&layer=mapnik&marker=3.8750%2C11.5250"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
        />
      </section>
    </div>
  );
}
